<script lang="ts">
  import { ICON, Icon } from "@/libs/ui/Icon"
  import type { HTMLAttributes } from "svelte/elements"
  import { tweened } from "svelte/motion"
  import { type TransitionConfig } from "svelte/transition"

  let {
    min = 0,
    max = 1,
    optimum = max,
    value = (max - min) / 2,
    transition = () => ({}),
    ...props
  }: {
    min?: number
    max?: number
    optimum?: number
    value?: number
    transition?: (node: Element) => TransitionConfig
  } & HTMLAttributes<HTMLDivElement> = $props()

  const v = tweened(0, { duration: 100 })
  const count = 7

  $effect(() => {
    $v = Math.max(min, Math.min(value, max))
  })

  let bars = $derived(
    new Array(count).fill(null).map((_, idx) => {
      const value = ($v - min) / (max - min) // [min, max] -> [0, 1]
      const complete = value * (count - 1)

      let fill = 0,
        scale = 0

      if (idx <= complete) fill = 1
      else if (idx < complete + 1) fill = complete - Math.floor(complete)

      const r = Math.abs(complete - idx)
      if (r <= 2) scale = 1 - Math.min(0.78 * r, 1)

      return { fill, scale }
    }),
  )

  let isInOptimum = $derived($v >= optimum)
</script>

<div
  {...props}
  class={props.class}
  class:illumination-meter={/* anchor for external customization */ true}
  class:optimum={isInOptimum}
  transition:transition
>
  <Icon type={ICON.MOON} size={32} class="icon-illumination-low" />
  {#each bars as { fill, scale }}
    <div
      class="bar"
      style:--scale={scale}
      style:--fill={fill}
      style:--glow={Math.floor(fill)}
    ></div>
  {/each}
  <Icon type={ICON.SUN} size={32} class="icon-illumination-optimum" />
</div>

<style lang="postcss">
  .illumination-meter {
    @apply flex h-[3.625rem] w-[15.875rem] items-center gap-2.5 p-2 text-white;

    & > :global(:first-child),
    & > :global(:last-child) {
      @apply transition-opacity;
    }
    & > :global(:first-child) {
      @apply mr-auto;
    }
    & > :global(:last-child) {
      @apply ml-auto;
    }
    &.optimum > :global(:first-child) {
      @apply opacity-60;
    }
    &:not(.optimum) > :global(:last-child) {
      @apply opacity-60;
    }
  }

  :global([data-ui-scale="xs"]) .illumination-meter {
    @apply h-[3.25rem] w-[14.375rem];
  }

  .bar {
    @apply relative h-6 w-2 bg-current;

    --border-radius: 3px;
    --x-scale: calc(1 + 0.25 * (var(--scale, 0)));
    --y-scale: calc(1 + 0.75 * (var(--scale, 0)));
    --opacity: calc(0.6 + 0.4 * (var(--fill, 0)));

    opacity: var(--opacity);
    transform: scale(var(--x-scale), var(--y-scale));
    /* https://css-tricks.com/various-methods-for-expanding-a-box-while-preserving-the-border-radius/#aa-changing-scale */
    border-radius: calc(var(--border-radius) / var(--x-scale)) /
      calc(var(--border-radius) / var(--y-scale));

    /* glow */
    &::before {
      @apply pointer-events-none absolute inset-0 rounded-[inherit] transition-opacity content-[""];

      --s: (1 + var(--scale));

      opacity: var(--glow);
      box-shadow:
        0px 0px calc(24px / var(--s)) calc(-2px / var(--s)) currentColor,
        0px 0px calc(6px / var(--s)) calc(-1px / var(--s)) currentColor;
    }
  }

  :global([data-ui-scale="xs"]) .bar {
    @apply h-5 w-1.5;

    --border-radius: 2px;
  }
</style>
